/**
 * Mock data for global search
 */

import type { CardSearchResult, MockProject, MockWorkspace } from './types';
import { SearchResultType, CardStatus, Priority } from './types';

// Mock workspaces
export const mockWorkspaces: MockWorkspace[] = [
  {
    id: 'ws-1',
    name: 'Acme Corporation',
    description: 'Main workspace for Acme Corporation projects and teams',
    memberCount: 24,
    projectCount: 8,
  },
  {
    id: 'ws-2',
    name: 'Personal Projects',
    description: 'Your personal workspace for side projects and experiments',
    memberCount: 1,
    projectCount: 5,
  },
  {
    id: 'ws-3',
    name: 'Client Work',
    description: 'External client projects and deliverables',
    memberCount: 12,
    projectCount: 4,
  },
];

// Mock projects
export const mockProjects: MockProject[] = [
  {
    id: 'proj-1',
    name: 'Website Redesign',
    description: 'Complete overhaul of the company website with modern design',
    workspaceId: 'ws-1',
    workspaceName: 'Acme Corporation',
    cardCount: 42,
  },
  {
    id: 'proj-2',
    name: 'Mobile App Development',
    description: 'Native iOS and Android apps for customer engagement',
    workspaceId: 'ws-1',
    workspaceName: 'Acme Corporation',
    cardCount: 78,
  },
  {
    id: 'proj-3',
    name: 'Q4 Marketing Campaign',
    description: 'End-of-year marketing initiatives and promotional materials',
    workspaceId: 'ws-1',
    workspaceName: 'Acme Corporation',
    cardCount: 35,
  },
  {
    id: 'proj-4',
    name: 'Internal Tools',
    description: 'Building tools to improve team productivity',
    workspaceId: 'ws-1',
    workspaceName: 'Acme Corporation',
    cardCount: 28,
  },
  {
    id: 'proj-5',
    name: 'Portfolio Site',
    description: 'Personal portfolio website to showcase projects',
    workspaceId: 'ws-2',
    workspaceName: 'Personal Projects',
    cardCount: 15,
  },
];

// Mock cards
export const mockCards: CardSearchResult[] = [
  {
    id: 'card-1',
    type: SearchResultType.CARD,
    title: 'Design homepage mockups',
    identifier: 'WEB-101',
    description: 'Create high-fidelity mockups for the new homepage design',
    status: CardStatus.IN_PROGRESS,
    priority: Priority.HIGH,
    projectId: 'proj-1',
    projectName: 'Website Redesign',
    workspaceId: 'ws-1',
    workspaceName: 'Acme Corporation',
    assigneeNames: ['Alice Chen', 'Bob Smith'],
  },
  {
    id: 'card-2',
    type: SearchResultType.CARD,
    title: 'Implement user authentication',
    identifier: 'APP-205',
    description: 'Set up OAuth2 and JWT-based authentication flow',
    status: CardStatus.TODO,
    priority: Priority.HIGHEST,
    projectId: 'proj-2',
    projectName: 'Mobile App Development',
    workspaceId: 'ws-1',
    workspaceName: 'Acme Corporation',
    assigneeNames: ['Charlie Davis'],
  },
  {
    id: 'card-3',
    type: SearchResultType.CARD,
    title: 'Create social media assets',
    identifier: 'MKT-156',
    description: 'Design graphics for Instagram, Twitter, and LinkedIn campaigns',
    status: CardStatus.DONE,
    priority: Priority.MEDIUM,
    projectId: 'proj-3',
    projectName: 'Q4 Marketing Campaign',
    workspaceId: 'ws-1',
    workspaceName: 'Acme Corporation',
    assigneeNames: ['Diana Prince'],
  },
  {
    id: 'card-4',
    type: SearchResultType.CARD,
    title: 'Set up CI/CD pipeline',
    identifier: 'TOOL-42',
    description: 'Configure GitHub Actions for automated testing and deployment',
    status: CardStatus.IN_PROGRESS,
    priority: Priority.HIGH,
    projectId: 'proj-4',
    projectName: 'Internal Tools',
    workspaceId: 'ws-1',
    workspaceName: 'Acme Corporation',
    assigneeNames: ['Eve Johnson'],
  },
  {
    id: 'card-5',
    type: SearchResultType.CARD,
    title: 'Write project documentation',
    identifier: 'WEB-112',
    description: 'Document the new design system and component library',
    status: CardStatus.BACKLOG,
    priority: Priority.LOW,
    projectId: 'proj-1',
    projectName: 'Website Redesign',
    workspaceId: 'ws-1',
    workspaceName: 'Acme Corporation',
    assigneeNames: [],
  },
  {
    id: 'card-6',
    type: SearchResultType.CARD,
    title: 'Fix navigation bug on mobile',
    identifier: 'APP-218',
    description: 'Menu not closing when tapping outside on iOS devices',
    status: CardStatus.REVIEW,
    priority: Priority.URGENT,
    projectId: 'proj-2',
    projectName: 'Mobile App Development',
    workspaceId: 'ws-1',
    workspaceName: 'Acme Corporation',
    assigneeNames: ['Frank Miller'],
  },
  {
    id: 'card-7',
    type: SearchResultType.CARD,
    title: 'Design hero section',
    identifier: 'PORT-1',
    description: 'Create an eye-catching hero section for portfolio site',
    status: CardStatus.IN_PROGRESS,
    priority: Priority.MEDIUM,
    projectId: 'proj-5',
    projectName: 'Portfolio Site',
    workspaceId: 'ws-2',
    workspaceName: 'Personal Projects',
    assigneeNames: ['Your Name'],
  },
  {
    id: 'card-8',
    type: SearchResultType.CARD,
    title: 'Implement dark mode',
    identifier: 'PORT-2',
    description: 'Add dark mode toggle with system preference detection',
    status: CardStatus.TODO,
    priority: Priority.LOW,
    projectId: 'proj-5',
    projectName: 'Portfolio Site',
    workspaceId: 'ws-2',
    workspaceName: 'Personal Projects',
    assigneeNames: ['Your Name'],
  },
  {
    id: 'card-9',
    type: SearchResultType.CARD,
    title: 'Optimize database queries',
    identifier: 'TOOL-38',
    description: 'Add indexes and optimize slow queries in reporting module',
    status: CardStatus.IN_PROGRESS,
    priority: Priority.HIGH,
    projectId: 'proj-4',
    projectName: 'Internal Tools',
    workspaceId: 'ws-1',
    workspaceName: 'Acme Corporation',
    assigneeNames: ['Grace Hopper'],
  },
  {
    id: 'card-10',
    type: SearchResultType.CARD,
    title: 'Create email templates',
    identifier: 'MKT-160',
    description: 'Design responsive email templates for newsletter campaign',
    status: CardStatus.TODO,
    priority: Priority.MEDIUM,
    projectId: 'proj-3',
    projectName: 'Q4 Marketing Campaign',
    workspaceId: 'ws-1',
    workspaceName: 'Acme Corporation',
    assigneeNames: ['Henry Ford'],
  },
  {
    id: 'card-11',
    type: SearchResultType.CARD,
    title: 'User research interviews',
    identifier: 'WEB-98',
    description: 'Conduct interviews with 10 potential users',
    status: CardStatus.DONE,
    priority: Priority.HIGH,
    projectId: 'proj-1',
    projectName: 'Website Redesign',
    workspaceId: 'ws-1',
    workspaceName: 'Acme Corporation',
    assigneeNames: ['Iris West'],
  },
  {
    id: 'card-12',
    type: SearchResultType.CARD,
    title: 'Set up push notifications',
    identifier: 'APP-230',
    description: 'Implement Firebase Cloud Messaging for push notifications',
    status: CardStatus.BACKLOG,
    priority: Priority.LOW,
    projectId: 'proj-2',
    projectName: 'Mobile App Development',
    workspaceId: 'ws-1',
    workspaceName: 'Acme Corporation',
    assigneeNames: [],
  },
  {
    id: 'card-13',
    type: SearchResultType.CARD,
    title: 'A/B test landing pages',
    identifier: 'MKT-145',
    description: 'Run A/B tests on new landing page variations',
    status: CardStatus.IN_PROGRESS,
    priority: Priority.HIGH,
    projectId: 'proj-3',
    projectName: 'Q4 Marketing Campaign',
    workspaceId: 'ws-1',
    workspaceName: 'Acme Corporation',
    assigneeNames: ['Jack Ryan'],
  },
  {
    id: 'card-14',
    type: SearchResultType.CARD,
    title: 'Build dashboard widgets',
    identifier: 'TOOL-45',
    description: 'Create customizable dashboard widgets for analytics',
    status: CardStatus.REVIEW,
    priority: Priority.MEDIUM,
    projectId: 'proj-4',
    projectName: 'Internal Tools',
    workspaceId: 'ws-1',
    workspaceName: 'Acme Corporation',
    assigneeNames: ['Kate Bishop'],
  },
  {
    id: 'card-15',
    type: SearchResultType.CARD,
    title: 'Add project case studies',
    identifier: 'PORT-5',
    description: 'Write and add case studies for top 3 projects',
    status: CardStatus.TODO,
    priority: Priority.MEDIUM,
    projectId: 'proj-5',
    projectName: 'Portfolio Site',
    workspaceId: 'ws-2',
    workspaceName: 'Personal Projects',
    assigneeNames: ['Your Name'],
  },
  {
    id: 'card-16',
    type: SearchResultType.CARD,
    title: 'Integrate payment gateway',
    identifier: 'APP-240',
    description: 'Set up Stripe integration for in-app purchases',
    status: CardStatus.IN_PROGRESS,
    priority: Priority.HIGHEST,
    projectId: 'proj-2',
    projectName: 'Mobile App Development',
    workspaceId: 'ws-1',
    workspaceName: 'Acme Corporation',
    assigneeNames: ['Leo Messi'],
  },
  {
    id: 'card-17',
    type: SearchResultType.CARD,
    title: 'SEO optimization',
    identifier: 'WEB-120',
    description: 'Improve page speed and meta tags for better SEO',
    status: CardStatus.TODO,
    priority: Priority.MEDIUM,
    projectId: 'proj-1',
    projectName: 'Website Redesign',
    workspaceId: 'ws-1',
    workspaceName: 'Acme Corporation',
    assigneeNames: ['Maria Garcia'],
  },
  {
    id: 'card-18',
    type: SearchResultType.CARD,
    title: 'Fix responsive issues',
    identifier: 'WEB-115',
    description: 'Address layout problems on tablet and mobile devices',
    status: CardStatus.REVIEW,
    priority: Priority.HIGH,
    projectId: 'proj-1',
    projectName: 'Website Redesign',
    workspaceId: 'ws-1',
    workspaceName: 'Acme Corporation',
    assigneeNames: ['Nancy Drew'],
  },
  {
    id: 'card-19',
    type: SearchResultType.CARD,
    title: 'Analytics integration',
    identifier: 'PORT-8',
    description: 'Add Google Analytics and custom event tracking',
    status: CardStatus.BACKLOG,
    priority: Priority.LOW,
    projectId: 'proj-5',
    projectName: 'Portfolio Site',
    workspaceId: 'ws-2',
    workspaceName: 'Personal Projects',
    assigneeNames: [],
  },
  {
    id: 'card-20',
    type: SearchResultType.CARD,
    title: 'Performance monitoring setup',
    identifier: 'TOOL-50',
    description: 'Configure Sentry and New Relic for error tracking',
    status: CardStatus.DONE,
    priority: Priority.HIGH,
    projectId: 'proj-4',
    projectName: 'Internal Tools',
    workspaceId: 'ws-1',
    workspaceName: 'Acme Corporation',
    assigneeNames: ['Oscar Wilde'],
  },
];

// Re-export types for convenience
export { SearchResultType, CardStatus, Priority };
